import Check from "@mui/icons-material/Check";
import Edit from "@mui/icons-material/Edit";
import EditOff from "@mui/icons-material/EditOff";
import WarningIcon from "@mui/icons-material/Warning";
import { Tooltip } from "@mui/material";
import {
  GridActionsCellItem,
  GridAlignment,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { computeWillReport } from "encamp-shared/src/tierii/computeWillReport";
import {
  prettyPrintAmountCode,
  prettyPrintQuantity,
} from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import { Issue, ReportingFacilityChemicalsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useCallback, useMemo, useState } from "react";
import { formatChemicalName } from "util/chemicalName";
import { anySegment } from "util/issues";

import { useFacility } from "../../Facility/useFacility";
import { portalEhsColumns } from "../portalEhsColumn";
import { useReportIsVerified } from "../Report/useReportIsVerified";
import { ReportingFacilityChemicalEditor } from "./ReportingFacilityChemicalEditor";

type Row = ReportingFacilityChemicalsQuery["reportingFacilityChemicals"]["items"][number];

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];
const EXCLUDE_FILTER_COLUMNS = ["issues"];
const getItems = (data: ReportingFacilityChemicalsQuery) => data.reportingFacilityChemicals.items;
const getCount = (data: ReportingFacilityChemicalsQuery) => data.reportingFacilityChemicals.count;

export const REPORTING_FACILITY_CHEMICALS_QUERY = gql(`
  query ReportingFacilityChemicals($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    reportingFacilityChemicals(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        chemical {
          ...ChemicalPicker
          alternateId
          isEhs
          portalIsEhs
          noHazardsNotReporting
        }
        facility {
          state
        }
        maxAmount
        maxAmountCode
        averageAmount
        averageAmountCode
        unit
        isOverThreshold
        isAlwaysReported
        isExempt
        willReport
        issues {
          ...issue
        }
        aggregationErrorMessage
        aggregationError
      }
      count
    }
  }
`);

function useReportingFacilityChemicalColumns({
  isVerified,
  facilityState,
  onOpen,
}: {
  isVerified: boolean;
  facilityState: string | null | undefined;
  onOpen: (reportingFacilityChemicalId: string) => void;
}) {
  return useMemo<OmnisearchGridColDef<Row>[]>(() => {
    const issuesColumn: OmnisearchGridColDef<Row>[] = isVerified
      ? []
      : [
          {
            field: "issues",
            headerName: "Issues",
            align: "center",
            headerAlign: "center",
            renderCell: ({ row }) => {
              if (row.aggregationErrorMessage) {
                const problematicChemicals = (
                  row.aggregationError as
                    | { problematicChemicals?: { id: string; name: string }[] }
                    | undefined
                )?.problematicChemicals;
                const tooltipText = problematicChemicals?.length
                  ? `${row.aggregationErrorMessage} Fix in catalog: ${problematicChemicals.map((c) => c.name).join(", ")}`
                  : row.aggregationErrorMessage;
                return (
                  <Tooltip title={tooltipText}>
                    <WarningIcon color="error" />
                  </Tooltip>
                );
              }

              return (
                <IssueCount
                  ignoreForReport={!(row.willReport ?? computeWillReport(row))}
                  issueCount={
                    row?.issues?.filter(
                      (issue: Issue) =>
                        !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
                    ).length
                  }
                />
              );
            },
            sortable: false,
            flex: 0.8,
          },
        ];

    return [
      {
        field: "name",
        headerName: "Chemical",
        flex: 2.0,
        renderCell: ({ row }) => {
          if (!row.chemical) return "";
          return (
            <TextCellWithTooltip tooltip={formatChemicalName(row.chemical)}>
              {row.chemical?.name}
            </TextCellWithTooltip>
          );
        },
      },
      {
        field: "casNumber",
        headerName: "CAS #",
        flex: 1,
        renderCell: ({ row }) => (
          <TextCellWithTooltip>{row.chemical?.casNumber}</TextCellWithTooltip>
        ),
      },
      {
        field: "alternateId",
        headerName: "Alt ID",
        flex: 1,
        renderCell: ({ row }) => (
          <TextCellWithTooltip>{row.chemical?.alternateId}</TextCellWithTooltip>
        ),
      },
      {
        field: "isEhs",
        headerName: "Is EHS",
        align: "center" as GridAlignment,
        headerAlign: "center" as GridAlignment,
        filterKeyType: "boolean",
        flex: 0.7,
        renderCell({ row }) {
          if (row.chemical?.isEhs) return <Check />;
          return <></>;
        },
      },
      ...portalEhsColumns<Row>(facilityState),
      {
        field: "maxAmount",
        headerName: "Max Amt",
        filterKeyType: "number",
        flex: 1,
        valueGetter: (_, row) => {
          if (row.aggregationErrorMessage) {
            return "--";
          }
          if (row.maxAmount) {
            return prettyPrintQuantity({
              amount: row.maxAmount,
              unit: row.unit,
              state: row.facility?.state,
            });
          } else if (row.maxAmountCode != null) {
            return prettyPrintAmountCode(row.facility?.state, row.maxAmountCode, row.unit, true);
          }
          return "";
        },
        renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
      },
      {
        field: "averageAmount",
        headerName: "Avg Amt",
        filterKeyType: "number",
        flex: 1,
        valueGetter: (_, row) => {
          if (row.aggregationErrorMessage) {
            return "--";
          }
          if (row.averageAmount) {
            return prettyPrintQuantity({
              amount: row.averageAmount,
              unit: row.unit,
              state: row.facility?.state,
            });
          } else if (row.averageAmountCode != null) {
            return prettyPrintAmountCode(
              row.facility?.state,
              row.averageAmountCode,
              row.unit,
              true
            );
          }
          return "";
        },
        renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
      },
      {
        field: "isOverThreshold",
        headerName: "Over Threshold",
        align: "center",
        headerAlign: "center",
        filterKeyType: "boolean",
        flex: 1,
        renderCell({ row }) {
          if (row.isOverThreshold) return <Check />;
          return <></>;
        },
      },
      {
        field: "willReport",
        headerName: "Will Report",
        headerAlign: "center",
        align: "center",
        flex: 0.7,
        filterKeyType: "boolean",
        renderCell({ row }) {
          const willReport = row.willReport ?? computeWillReport(row);
          return willReport ? <Check /> : <></>;
        },
      },
      ...issuesColumn,
      {
        field: "actions",
        type: "actions",
        getActions: ({ row }) => [
          <Tooltip title={isVerified ? "Read-only" : "Open preview"} key={1}>
            <GridActionsCellItem
              onClick={() => onOpen(row.id)}
              label={isVerified ? "Read-only" : "Open preview"}
              icon={
                isVerified ? (
                  <EditOff sx={{ color: (theme) => theme.palette.grey[500] }} />
                ) : (
                  <Edit />
                )
              }
            />
          </Tooltip>,
        ],
      },
    ];
  }, [isVerified, facilityState, onOpen]);
}

export function ReportingFacilityChemicalTable({
  facilityId,
  reportId,
}: {
  facilityId: string;
  reportId?: string;
}) {
  const isVerified = useReportIsVerified();
  const facilityState = useFacility(facilityId).data?.facility?.state;

  const reportingYear = useSelectedReportingYear();
  const [selectedRfcId, setSelectedRfcId] = useState<undefined | string>(undefined);

  const columns = useReportingFacilityChemicalColumns({
    isVerified,
    facilityState,
    onOpen: setSelectedRfcId,
  });

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const reportFilter = reportId ? ` reportId:${reportId}` : "";
      const defaults = `facilityId:${facilityId}${reportFilter} reportingYear:${reportingYear}`;
      return {
        search: omnisearch ? `${omnisearch} ${defaults}` : defaults,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [facilityId, reportId, reportingYear]
  );

  const commandButtons = useMemo(
    () => [
      !reportId ? (
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            size: "small",
          }}
          persistToLocalStorage
        />
      ) : undefined,
    ],
    [reportId]
  );

  const handleRowClick = useCallback(
    ({ row: { id } }: GridRowParams<Row>) => setSelectedRfcId(id),
    []
  );

  return (
    <>
      <OmnisearchDataGridV2
        persistenceKey="reporting-facility-chemicals"
        columns={columns}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={25}
        query={REPORTING_FACILITY_CHEMICALS_QUERY}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        onRowClick={handleRowClick}
        noRowsMessage="No chemicals found."
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        compact
        commandButtons={commandButtons}
      />
      {selectedRfcId && (
        <ReportingFacilityChemicalEditor
          id={selectedRfcId}
          reportId={reportId}
          onClose={() => setSelectedRfcId(undefined)}
        />
      )}
    </>
  );
}
